import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight, Instagram, Linkedin } from 'lucide-react';
import { useContent } from '../context/ContentContext';
import { trackButtonClick } from '../services/api';

const Header = () => {
  const { siteConfig, heroContent } = useContent();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isFollowOpen, setIsFollowOpen] = useState(false);
  const followRef = useRef(null);

  // Custom navigation items (removed About, added Contact, Inquiry, Follow)
  const customNavItems = [
    { name: "Framework", href: "#framework" },
    { name: "Programs", href: "#programs" },
    { name: "Contact", href: "#footer-contact" },
    { name: "Inquiry", href: "#contact-form" },
  ];

  const socialLinks = [
    { name: "Instagram", href: "https://www.instagram.com/commfluentbiz/", icon: Instagram },
    { name: "LinkedIn", href: "https://www.linkedin.com/company/commfluentbiz/", icon: Linkedin },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close follow dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (followRef.current && !followRef.current.contains(event.target)) {
        setIsFollowOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBookCallClick = () => {
    trackButtonClick('header-book-call', 'Book a Call', '/');
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-[var(--bg-overlay)] backdrop-blur-lg border-b border-[var(--border-primary)]'
            : 'bg-transparent'
        }`}
      >
        <div className="container-main">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <a href="#" className="flex items-center gap-3">
              <img
                src={siteConfig.logo}
                alt={siteConfig.name}
                className="h-14 sm:h-16 md:h-20 lg:h-24 w-auto object-contain"
              />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6 lg:gap-8">
              {customNavItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="nav-link text-sm"
                >
                  {item.name}
                </a>
              ))}
              
              {/* Follow Dropdown */}
              <div className="relative" ref={followRef}>
                <button
                  onClick={() => setIsFollowOpen(!isFollowOpen)}
                  className="nav-link text-sm flex items-center gap-1"
                >
                  Follow
                  <svg 
                    className={`w-4 h-4 transition-transform ${isFollowOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                <AnimatePresence>
                  {isFollowOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full right-0 mt-2 w-48 bg-[var(--bg-card)] border border-[var(--border-primary)] rounded-lg shadow-xl overflow-hidden"
                    >
                      {socialLinks.map((social) => (
                        <a
                          key={social.name}
                          href={social.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 px-4 py-3 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-section)] transition-colors"
                          onClick={() => setIsFollowOpen(false)}
                        >
                          <social.icon className="w-5 h-5 text-[var(--accent-teal)]" />
                          {social.name}
                        </a>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-4">
              <a
                href={heroContent.calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleBookCallClick}
                className="btn-primary text-sm"
                data-testid="header-book-call-btn"
              >
                Book a Call
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-[var(--text-primary)]"
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-[var(--bg-page)] pt-20"
            data-testid="mobile-menu"
          >
            <nav className="container-main py-8">
              <div className="flex flex-col gap-6">
                {customNavItems.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-xl font-medium text-[var(--text-primary)] py-2 border-b border-[var(--border-primary)]"
                  >
                    {item.name}
                  </a>
                ))}
                
                {/* Social Links in Mobile */}
                <div className="py-2 border-b border-[var(--border-primary)]">
                  <span className="text-sm text-[var(--text-muted)] mb-3 block">Follow Us</span>
                  <div className="flex gap-4">
                    {socialLinks.map((social) => (
                      <a
                        key={social.name}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-[var(--text-primary)] hover:text-[var(--accent-teal)] transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <social.icon className="w-6 h-6" />
                        <span className="text-sm">{social.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
                
                <a
                  href={heroContent.calendlyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary mt-4 w-full justify-center"
                  onClick={() => { setIsMobileMenuOpen(false); handleBookCallClick(); }}
                  data-testid="mobile-book-call-btn"
                >
                  Book a Strategy Call
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
